import type { DynamicPath } from '@unifyapps/defs/types/page';
import { createDraft, finishDraft } from 'immer';
import _get from 'lodash/get';
import _set from 'lodash/set';
import { ComputeContextKeys } from '../../../hooks/computeContext/const';

// Regex to match envVariables['varName'] pattern
const EXTRACT_ENV_VARIABLE_NAME_REGEX = new RegExp(
  `${ComputeContextKeys.ENV_VARIABLES}\\['(?<varName>[^']+)'\\]`,
  'g',
);

function convertEnvVariableFormat(input: string): string {
  // Replace all occurrences of the pattern
  return input.replace(EXTRACT_ENV_VARIABLE_NAME_REGEX, (match, varName: string) => {
    return `__ENV__.outputs.${varName}`;
  });
}

export const addEnvVariablesRegex = <T>({
  data,
  computedData,
  dynamicPaths,
}: {
  data: Record<string, unknown>;
  computedData: Record<string, unknown>;
  dynamicPaths?: DynamicPath[];
}): T => {
  /**
   * We do not want to add env variable's resolved value when they are mapped in data source
   * since env variables can be senstive, we do not fetch values of sensitive env variables on client.
   *
   * We allow sensitive env variables to be mapped in data source.
   * Backend resolves the value of sensitive env variables, if we send env variable according to below regex
   * {{ __ENV__.outputs.${envVarName} }}
   * */
  if (!dynamicPaths) {
    return computedData as T;
  }

  const adaptedData = createDraft(computedData);

  dynamicPaths.forEach(({ p: path }) => {
    const valueAtPath = _get(data, path);

    if (typeof valueAtPath === 'string' && valueAtPath.includes(ComputeContextKeys.ENV_VARIABLES)) {
      // replace the env variable with the regex that BE expects
      _set(adaptedData, path, convertEnvVariableFormat(valueAtPath));
    }
  });

  return finishDraft(adaptedData) as T;
};
